import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import NoData from '../../../components/common/NoData';

import { getNotifications } from '../../../services/user/notification.services';
import { hasNotificationOpenLink, resolveNotificationTarget } from '../../../utils/notificationDeepLink';
import Loader from '../../../components/common/Loader';
import { useTimezone } from '../../../context/TimezoneContext';
import { formatUtcInTimeZone } from '../../../utils/timezone';

function parseContentJson(contentJson) {
  if (!contentJson) {
    return {};
  }

  if (typeof contentJson === 'string') {
    try {
      return JSON.parse(contentJson);
    } catch {
      return {};
    }
  }

  return contentJson;
}

function normalizeNotification(raw) {
  if (!raw) {
    return null;
  }

  const contentJson = parseContentJson(raw.content_json);

  return {
    ...raw,
    title: raw.title || contentJson?.notification?.title || 'Notification',
    body: raw.body || contentJson?.notification?.body || '',
    deepLink: raw.deepLink || contentJson?.data?.deepLink || null,
    course_id: raw.course_id || contentJson?.data?.courseId || contentJson?.data?.course_id || null,
    event: raw.event || contentJson?.data?.eventType || null,
  };
}

function NotificationDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { timeZone } = useTimezone();
  const role = localStorage.getItem('role');
  const notificationFromState = location.state?.notification;
  const [notification, setNotification] = useState(() => normalizeNotification(notificationFromState));
  const [loading, setLoading] = useState(!notificationFromState);
  const [notFound, setNotFound] = useState(false);

  const normalizedNotification = useMemo(
    () => normalizeNotification(notification),
    [notification]
  );
  const relatedPageLink = useMemo(
    () => (normalizedNotification ? resolveNotificationTarget(normalizedNotification, role) : null),
    [normalizedNotification, role]
  );

  useEffect(() => {
    let cancelled = false;

    const loadNotification = async () => {
      if (notificationFromState) {
        setNotification(normalizeNotification(notificationFromState));
        setLoading(false);
        return;
      }

      if (!id) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await getNotifications({ page: 1, limit: 100 });
        const found = (response.notifications || []).find(
          (item) => String(item.id) === String(id)
        );

        if (cancelled) {
          return;
        }

        if (found) {
          setNotification(normalizeNotification(found));
          setNotFound(false);
        } else {
          setNotFound(true);
        }
      } catch (error) {
        console.error('Error fetching notification details:', error);
        if (!cancelled) {
          setNotFound(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadNotification();

    return () => {
      cancelled = true;
    };
  }, [id, notificationFromState]);

  if (loading) {
    return (
      <div className="tw-m-[0_auto] notification-details-container tw-text-center tw-py-12">
        <Loader />
      </div>
    );
  }

  if (!normalizedNotification || notFound) {
    return (
      <div className="tw-m-[0_auto] notification-details-container">
        <NoData
          message="Notification not found"
          description="The notification you are looking for is unavailable or was removed."
        />
        <div className="tw-text-center tw-pb-6">
          <button
            type="button"
            className={"tw-inline-flex tw-items-center tw-gap-[8px] tw-p-[10px_20px] tw-bg-[#f0f0f0] tw-text-[#333] tw-rounded-[6px] tw-no-underline tw-font-medium [transition:background-color_0.2s] [&:hover]:tw-bg-[#e0e0e0] back-to-notifications"}
            onClick={() => navigate('/admin/notifications')}
          >
            <Icon icon="mdi:arrow-left" width="18" height="18" />
            Back to notifications
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row"}>
      <div className="tw-min-w-[0px] ui-column tw-w-full">
        <div className="tw-m-[0_auto] notification-details-container">
          <div className={"tw-flex tw-items-center tw-mb-[24px] tw-gap-[20px] [&_h2]:tw-m-0 [&_h2]:tw-text-[24px] [&_h2]:tw-text-[#333] notification-details-header"}>
            <button
              type="button"
              className={"tw-flex tw-items-center tw-gap-[8px] tw-p-[8px_16px] tw-border-0 tw-bg-[#f5f5f5] tw-rounded-[8px] tw-text-[#333] tw-cursor-pointer [transition:all_0.2s_ease] [&:hover]:tw-bg-[#e5e5e5] back-button"}
              onClick={() => navigate('/admin/notifications')}
            >
              <Icon icon="mdi:arrow-left" width="18" height="18" />
              Back
            </button>
          </div>

          <div className="tw-bg-[#fff] tw-rounded-[10px] tw-p-[20px] tw-shadow-[0_2px_4px_rgba(0,_0,_0,_0.1)] max-[768px]:tw-p-[16px] notification-details-card">
            <div className="tw-mb-[30px] notification-details-content">
              <div className={"tw-mb-[20px] [&_label]:tw-font-semibold [&_label]:tw-text-[#666] [&_label]:tw-block [&_label]:tw-mb-[5px] [&_h3]:tw-m-0 [&_h3]:tw-text-[20px] [&_h3]:tw-text-[#333] [&_p]:tw-m-0 [&_p]:tw-text-[16px] [&_p]:tw-text-[#444] [&_p]:tw-leading-[1.5] detail-group"}>
                <label>Title</label>
                <h3>{normalizedNotification.title}</h3>
              </div>

              <div className={"tw-mb-[20px] [&_label]:tw-font-semibold [&_label]:tw-text-[#666] [&_label]:tw-block [&_label]:tw-mb-[5px] [&_h3]:tw-m-0 [&_h3]:tw-text-[20px] [&_h3]:tw-text-[#333] [&_p]:tw-m-0 [&_p]:tw-text-[16px] [&_p]:tw-text-[#444] [&_p]:tw-leading-[1.5] detail-group"}>
                <label>Message</label>
                <p>{normalizedNotification.body || 'No message provided.'}</p>
              </div>

              <div className={"tw-mb-[20px] [&_label]:tw-font-semibold [&_label]:tw-text-[#666] [&_label]:tw-block [&_label]:tw-mb-[5px] [&_h3]:tw-m-0 [&_h3]:tw-text-[20px] [&_h3]:tw-text-[#333] [&_p]:tw-m-0 [&_p]:tw-text-[16px] [&_p]:tw-text-[#444] [&_p]:tw-leading-[1.5] detail-group"}>
                <label>Date</label>
                <p>
                  {normalizedNotification.createdAt
                    ? formatUtcInTimeZone(normalizedNotification.createdAt, timeZone, 'MMM DD, YYYY, hh:mm A')
                    : '-'}
                </p>
              </div>

              {relatedPageLink && hasNotificationOpenLink(normalizedNotification, role) && (
                <div className={"tw-mb-[20px] [&_label]:tw-font-semibold [&_label]:tw-text-[#666] [&_label]:tw-block [&_label]:tw-mb-[5px] [&_h3]:tw-m-0 [&_h3]:tw-text-[20px] [&_h3]:tw-text-[#333] [&_p]:tw-m-0 [&_p]:tw-text-[16px] [&_p]:tw-text-[#444] [&_p]:tw-leading-[1.5] detail-group"}>
                  <label>Open related page</label>
                  <button
                    type="button"
                    className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-text-[0.875rem] tw-font-medium tw-rounded-[0.5rem] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[1px] tw-border-[transparent] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] tw-no-underline tw-m-0 tw-text-[rgb(64_84_214_/_var(--tw-text-opacity,_1))] [transition:opacity_0.2s_ease] tw-bg-[transparent] [--tw-text-opacity:1] [&:hover]:tw-opacity-[0.8] [&:hover]:tw-text-[#dc3545] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] ui-button ui-button-link tw-p-0"}
                    onClick={() => navigate(relatedPageLink)}
                  >
                    Open notification link
                  </button>
                </div>
              )}

              {normalizedNotification.file_url && (
                <div className={"tw-mb-[20px] [&_label]:tw-font-semibold [&_label]:tw-text-[#666] [&_label]:tw-block [&_label]:tw-mb-[5px] [&_h3]:tw-m-0 [&_h3]:tw-text-[20px] [&_h3]:tw-text-[#333] [&_p]:tw-m-0 [&_p]:tw-text-[16px] [&_p]:tw-text-[#444] [&_p]:tw-leading-[1.5] detail-group"}>
                  <label>Attachment</label>
                  <a
                    href={normalizedNotification.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={"tw-inline-flex tw-items-center tw-gap-[8px] tw-text-[#4070f4] tw-no-underline [&:hover]:tw-underline attachment-link"}
                  >
                    <Icon icon="mdi:file-document-outline" width="20" height="20" />
                    View Attachment
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NotificationDetails;
